// Clinical math + classification + variant selection.
//
// Pure functions. Same input → same output. No side effects, no localStorage,
// no DOM access. Everything in this module is unit-testable in isolation.
//
// Citations (locked 2026-05-08; denominator re-anchored 2026-08-18 after the
// Holowka paper was read in full — see clinical-content.md):
//   - CVAI denominator = GREATER diagonal (Graham et al, J Clin Med 2019;8(8):1097,
//     PMC6723372 — same STARscanner and same CHOA scale as the tiers below)
//   - CVAI 5-tier 3.5/6.25/8.75/11 (CHOA Plagiocephaly Severity Scale; Holowka et al,
//     J Craniofac Surg 2017;28(3):717-722, which states the tiers but NO formula)
//   - CI Cohen/MacLean 4-tier 76/81/85.5 (Cohen & MacLean, Craniosynostosis 2nd ed)
//
// Internal classification keys are kebab-case strings ("severe", "very-short-wide",
// etc.) — these are STABLE identifiers used by the variant picker. Display labels
// ("Severe flatness", "Very short / wide") live in `src/generated/clinical-content.ts`
// and are derived from the keys via CVAI_LABELS / CI_LABELS. This keeps logic and
// presentation decoupled: changing a label cannot break the picker.

// -----------------------------------------------------------------------------
// Types

/** Four head measurements, all in the same unit (typically mm). Ratios are unitless. */
export interface CalcInput {
  /** Anterior-posterior length (front-to-back). Must be > 0. */
  apLength: number;
  /** Right diagonal (typically right-frontal to left-occipital). Must be > 0. */
  rightDiagonal: number;
  /** Left diagonal (typically left-frontal to right-occipital). Must be > 0. */
  leftDiagonal: number;
  /** Bi-parietal width (medial-lateral). Must be > 0. */
  parietalWidth: number;
}

/**
 * 5-tier CVAI classification (CHOA scale). Lower = more symmetric.
 * `severe` and `very-severe` always route to summary variant A.
 */
export type CVAIClass = "balanced" | "mild" | "moderate" | "severe" | "very-severe";

/**
 * 4-tier CI classification (Cohen/MacLean). `typical` is the desired range;
 * the others all describe atypical proportion.
 */
export type CIClass = "long-narrow" | "typical" | "short-wide" | "very-short-wide";

/**
 * Custom summary paragraph variant. 19 unique variants cover all 20 CVAI×CI
 * cells (C is shared between Balanced+Typical and Mild+Typical). See
 * clinical-content.md Section 13 for the truth table and the routing rules.
 *
 * Naming convention:
 *   A1 / A2     — Severe / Very-severe CVAI
 *   B           — Moderate CVAI
 *   C           — Balanced or Mild CVAI + Typical CI
 *   D           — Balanced CVAI + atypical CI
 *   E           — Mild CVAI + atypical CI
 *
 *   no suffix   — Typical CI
 *   -long       — Long/narrow CI
 *   -short      — Short/wide CI
 *   -vsw        — Very short/wide CI
 */
export type SummaryVariant =
  | "A1"
  | "A1-long"
  | "A1-short"
  | "A1-vsw"
  | "A2"
  | "A2-long"
  | "A2-short"
  | "A2-vsw"
  | "B"
  | "B-long"
  | "B-short"
  | "B-vsw"
  | "C"
  | "D-long"
  | "D-short"
  | "D-vsw"
  | "E-long"
  | "E-short"
  | "E-vsw";

export interface CalcResults {
  /** CI as a percentage. e.g. 82.4 means parietalWidth is 82.4% of apLength. */
  ci: number;
  /** CVAI as a percentage. e.g. 5.2 means the diagonals differ by 5.2% of the GREATER one. */
  cvai: number;
  ciClass: CIClass;
  cvaiClass: CVAIClass;
  variant: SummaryVariant;
}

/** Thrown for any measurement that's <= 0, NaN, or Infinity. */
export class InvalidMeasurementError extends Error {
  constructor(field: keyof CalcInput | "diagonals", reason: string) {
    super(`Invalid measurement (${field}): ${reason}`);
    this.name = "InvalidMeasurementError";
  }
}

// -----------------------------------------------------------------------------
// Validation

function assertPositiveFinite(value: number, field: keyof CalcInput): void {
  if (!Number.isFinite(value)) {
    throw new InvalidMeasurementError(field, "must be a finite number");
  }
  if (value <= 0) {
    throw new InvalidMeasurementError(field, "must be greater than zero");
  }
}

function validateInput(input: CalcInput): void {
  assertPositiveFinite(input.apLength, "apLength");
  assertPositiveFinite(input.rightDiagonal, "rightDiagonal");
  assertPositiveFinite(input.leftDiagonal, "leftDiagonal");
  assertPositiveFinite(input.parietalWidth, "parietalWidth");
}

// -----------------------------------------------------------------------------
// Math

/**
 * Cephalic Index — overall head proportion.
 *
 *     CI = (parietalWidth / apLength) × 100
 *
 * Returns a percentage. Lower = longer/narrower; higher = shorter/wider.
 * 76-81 is the typical (mesocephalic) range.
 */
export function computeCI(input: CalcInput): number {
  validateInput(input);
  return (input.parietalWidth / input.apLength) * 100;
}

/**
 * Cranial Vault Asymmetry Index — side-to-side asymmetry.
 *
 *     CVAI = |rightDiagonal − leftDiagonal| / GREATER_DIAGONAL × 100
 *
 * PRIMARY CITATION — Graham T, Adams-Huet B, Gilbert N, Witthoff K, Gregory T,
 * Walsh M, "Effects of Initial Age and Severity on Cranial Remolding Orthotic
 * Treatment for Infants with Deformational Plagiocephaly," J Clin Med
 * 2019;8(8):1097 (PMC6723372, doi:10.3390/jcm8081097). Verbatim: CVAI is "the
 * absolute value of the difference in cranial diagonals (CVA), divided by the
 * greater diagonal, then multiplied by 100." That study measures on the same
 * STARscanner Holowka used and states "Severity categories were based on the
 * Children's Healthcare of Atlanta Plagiocephaly Severity Scale," citing Holowka
 * as its reference 15. Same instrument, same scale, greater diagonal, in one
 * document — which is why it anchors this and the CHOA handout never stands
 * alone again.
 *
 * Corroborating: CHOA's own sheet (ORTH 961942.kc.7/15) prints "CVAI = A-B x 100
 * / A or B (whichever is greater)" — the intended hand-measurement instrument,
 * since Holowka argues the scale's value is that CVAI "can be achieved quickly
 * by hand in a clinical setting". CAPPS Kids publishes these same five ranges
 * with "divided by Longer diagonal".
 *
 * Holowka et al, J Craniofac Surg 2017;28(3):717-722 — the source of the
 * 3.5/6.25/8.75/11 tiers — CONTAINS NO CVAI FORMULA. It defers with
 * "Descriptions of each of the variables and indices have been previously
 * detailed in the literature," and its Figure 4 is the severity table with the
 * ranges and no equation. Read end to end (all 6 pages plus every embedded
 * figure) on 2026-08-18. Do not cite it for the denominator in either direction.
 *
 * Why the shorter-diagonal case loses. Loveday's original and 20 of 30 papers
 * reviewed by Miyabayashi et al (J Craniofac Surg 2023;34(4):e369-e372,
 * PMC10205121) do divide by the shorter diagonal — but those are research
 * 3D-scanning groups importing the CHOA thresholds into a Loveday-convention
 * pipeline. PMC8999343 is the tell: it cites BOTH Graham and Holowka for its
 * cutoffs and then computes with the shorter diagonal. That mismatch is exactly
 * what Miyabayashi wrote his paper to flag; it is not evidence about how these
 * tiers were derived. Shorter is the majority convention in the literature at
 * large; greater is the convention of the scale this tool displays. We print
 * CHOA's tiers, so we use CHOA's denominator.
 *
 * A majority-of-literature argument does NOT reopen this. The only thing that
 * does is a shorter-diagonal formula from inside the CHOA/STARscanner chain —
 * meaning Plank et al 2006 (J Craniofac Surg 17:1084-1091) or Geil & Smith 2008
 * (J Prosthet Orthot 20:35-38), the two papers Holowka defers its variable
 * definitions to. Neither has been read.
 *
 * History: a 2026-08-17 branch flipped this to the shorter diagonal on a
 * search-engine paraphrase of Holowka's abstract that the paper does not
 * support. It was caught in review and never shipped. main has been on the
 * greater diagonal since v2.1.0.0, so no handout has ever been mis-tiered.
 *
 * Returns a percentage. 0 = perfectly symmetric.
 */
export function computeCVAI(input: CalcInput): number {
  validateInput(input);
  const longer = Math.max(input.rightDiagonal, input.leftDiagonal);
  const diff = Math.abs(input.rightDiagonal - input.leftDiagonal);
  return (diff / longer) * 100;
}

// -----------------------------------------------------------------------------
// Classification

/**
 * 5-tier CVAI classification (CHOA Plagiocephaly Severity Scale).
 * Inclusive lower-bound, exclusive upper-bound (matches the published ranges).
 *
 *   < 3.5         → balanced
 *   3.5  ≤ < 6.25 → mild
 *   6.25 ≤ < 8.75 → moderate (literature's helmet-consideration threshold)
 *   8.75 ≤ < 11   → severe
 *   ≥ 11          → very-severe
 */
export function classifyCVAI(cvai: number): CVAIClass {
  if (!Number.isFinite(cvai) || cvai < 0) {
    throw new InvalidMeasurementError("diagonals", `CVAI cannot be ${cvai}`);
  }
  if (cvai < 3.5) return "balanced";
  if (cvai < 6.25) return "mild";
  if (cvai < 8.75) return "moderate";
  if (cvai < 11) return "severe";
  return "very-severe";
}

/**
 * 4-tier CI classification (Cohen/MacLean).
 * Inclusive lower-bound, exclusive upper-bound — eliminates the gap that
 * exists if the published bands are read literally as discrete ranges.
 *
 *   < 76          → long-narrow      (dolichocephaly)
 *   76 ≤ < 81     → typical          (mesocephaly)
 *   81 ≤ < 85.5   → short-wide       (brachycephaly)
 *   ≥ 85.5        → very-short-wide  (hyperbrachycephaly)
 */
export function classifyCI(ci: number): CIClass {
  if (!Number.isFinite(ci) || ci <= 0) {
    throw new InvalidMeasurementError("apLength", `CI cannot be ${ci}`);
  }
  if (ci < 76) return "long-narrow";
  if (ci < 81) return "typical";
  if (ci < 85.5) return "short-wide";
  return "very-short-wide";
}

// -----------------------------------------------------------------------------
// Variant selection

/**
 * Picks one of 19 custom summary paragraph variants based on the CVAI/CI
 * classifications. Locked truth table — see clinical-content.md Section 13.
 *
 *                CVAI →  balanced   mild    moderate   severe    very-severe
 *  CI ↓
 *  long-narrow            D-long    E-long  B-long     A1-long   A2-long
 *  typical                C         C       B          A1        A2
 *  short-wide             D-short   E-short B-short    A1-short  A2-short
 *  very-short-wide        D-vsw     E-vsw   B-vsw      A1-vsw    A2-vsw
 *
 * Routing precedence: CVAI very-severe → A2 family; CVAI severe → A1 family;
 * CVAI moderate → B family; CVAI balanced/mild + typical CI → C; CVAI mild
 * + atypical CI → E family; CVAI balanced + atypical CI → D family.
 */
export function pickSummaryVariant(cvai: CVAIClass, ci: CIClass): SummaryVariant {
  if (cvai === "very-severe") {
    if (ci === "long-narrow") return "A2-long";
    if (ci === "typical") return "A2";
    if (ci === "short-wide") return "A2-short";
    return "A2-vsw";
  }
  if (cvai === "severe") {
    if (ci === "long-narrow") return "A1-long";
    if (ci === "typical") return "A1";
    if (ci === "short-wide") return "A1-short";
    return "A1-vsw";
  }
  if (cvai === "moderate") {
    if (ci === "long-narrow") return "B-long";
    if (ci === "typical") return "B";
    if (ci === "short-wide") return "B-short";
    return "B-vsw";
  }
  // CVAI is balanced or mild — typical CI collapses to C, atypical splits.
  if (ci === "typical") return "C";
  if (cvai === "mild") {
    if (ci === "long-narrow") return "E-long";
    if (ci === "short-wide") return "E-short";
    return "E-vsw";
  }
  // CVAI is balanced + atypical CI.
  if (ci === "long-narrow") return "D-long";
  if (ci === "short-wide") return "D-short";
  return "D-vsw";
}

// -----------------------------------------------------------------------------
// Top-level API

/** Round to the one decimal place the UI and PDF both display. */
function toDisplayPrecision(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Run the full pipeline: validate, compute both indices, classify both,
 * pick a summary variant. Single entry point for the UI to call.
 *
 * Both indices are rounded to display precision BEFORE classification. The
 * screen and the PDF print `toFixed(1)`, so classifying the raw value would let
 * the printed number contradict its own tier: a raw CVAI of 3.4999 prints as
 * "3.5" while classifying as balanced, next to a table saying 3.5 is mild.
 * Rounding once here means the number shown is always the number classified.
 */
export function calculate(input: CalcInput): CalcResults {
  const ci = toDisplayPrecision(computeCI(input));
  const cvai = toDisplayPrecision(computeCVAI(input));
  const ciClass = classifyCI(ci);
  const cvaiClass = classifyCVAI(cvai);
  const variant = pickSummaryVariant(cvaiClass, ciClass);
  return { ci, cvai, ciClass, cvaiClass, variant };
}
